
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTheme, useColor, useBrand } from '../context/ColorContext';
import { Search, ShoppingBag } from 'lucide-react';
import { categoryService } from '../services/api';
import { turkishSearch } from '../db/db';

const ProductSkeleton = ({ isDark }) => (
    <div className={`rounded-[2.5rem] p-8 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/5'} animate-pulse`}>
        <div className="flex gap-2 mb-6">
            <div className="h-4 w-16 bg-white/10 rounded-full" />
            <div className="h-4 w-16 bg-white/10 rounded-full" />
        </div>
        <div className="h-48 w-full bg-white/5 rounded-3xl my-8" />
        <div className="space-y-3">
            <div className="h-6 w-3/4 bg-white/20 rounded-lg" />
            <div className="h-4 w-full bg-white/10 rounded-md" />
            <div className="h-4 w-5/6 bg-white/10 rounded-md" />
        </div>
        <div className="pt-6 border-t border-white/10 mt-6 flex justify-between">
            <div className="h-4 w-16 bg-white/10 rounded-md" />
        </div>
    </div>
);

const Urunler = () => {
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { brandData, isDbLoading } = useColor();
    const { activeBrand } = useBrand();
    const [selectedCategory, setSelectedCategory] = useState("Tümü");
    const [searchQuery, setSearchQuery] = useState('');
    const isDark = theme === 'dark';

    const PRODUCTS = brandData.PRODUCTS;
    const { data: dbCategories } = useQuery({ queryKey: ['categories'], queryFn: categoryService.getAll });

    const categories = useMemo(() => {
        if (!dbCategories || !PRODUCTS) return ["Tümü"];

        // Find categories that are actually used by the products of the active brand
        const usedCategoryNames = new Set(PRODUCTS.flatMap(p => p.categoryNames || []));

        const filteredAndSortedCats = dbCategories
            .filter(c => usedCategoryNames.has(c.name))
            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        return ["Tümü", ...filteredAndSortedCats.map(c => c.name)];
    }, [dbCategories, PRODUCTS]);

    const filteredProducts = PRODUCTS.filter(p => {
        // Search in all fields
        const searchContent = [
            p.name,
            p.description,
            p.longDesc,
            p.features,
            p.application,
            p.dryingTime,
            p.consumption,
            p.storage,
            ...(p.categoryNames || [])
        ].filter(Boolean).join(' ');

        const matchesSearch = turkishSearch(searchContent, searchQuery);

        // If searching, ignore category filter for "global" results as requested
        // If not searching, use category filter
        const matchesCategory = searchQuery.trim() !== ''
            ? true
            : (selectedCategory === "Tümü" || p.categoryNames?.includes(selectedCategory));

        return matchesSearch && matchesCategory;
    });

    const mascotImages = {
        'dufa': '/img/dufa-kirpi-transparan.png',
        'genc': '/img/genc-maskot.png',
        'woodsol': '/img/woodsol-maskot.png'
    };

    return (
        <div className={`min-h-screen pt-32 pb-20 px-4 md:px-0 container mx-auto ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {/* Page Header */}
            <div className="text-center mb-16 relative">
                <span className="text-sm font-bold tracking-[0.3em] opacity-60 uppercase">{activeBrand.name} Ürün Kataloğu</span>
                <h1 className="text-4xl md:text-7xl font-black uppercase mt-4 mb-6 tracking-tighter">Ürünler</h1>
                <p className="max-w-2xl mx-auto opacity-70 text-lg font-light leading-relaxed">
                    {activeBrand.description} İhtiyacınız olan tüm çözümler burada.
                </p>
            </div>

            {/* Filter & Search Bar */}
            <div className={`flex flex-col md:flex-row items-center justify-between gap-6 mb-12 p-6 rounded-3xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/5'} backdrop-blur-xl`}>
                <div className="flex items-center gap-4 w-full md:w-auto">
                    <div className={`flex items-center gap-3 px-6 py-3 rounded-full border ${isDark ? 'border-white/10 bg-black/20' : 'border-black/5 bg-white/50'} w-full md:w-80`}>
                        <Search size={20} className="opacity-40" />
                        <input
                            type="text"
                            placeholder="Ürün Ara..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="bg-transparent border-none outline-none w-full font-medium"
                        />
                    </div>
                </div>

                <div className="flex items-center gap-4 w-full md:w-auto overflow-x-auto no-scrollbar pb-2 md:pb-0">
                    {categories.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setSelectedCategory(cat)}
                            className={`px-6 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all whitespace-nowrap ${selectedCategory === cat ? 'text-white shadow-lg' : (isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-black/5 hover:bg-black/10')}`}
                            style={selectedCategory === cat ? { backgroundColor: activeBrand.primaryColor } : {}}
                        >
                            {cat.toLocaleUpperCase('tr-TR')}
                        </button>
                    ))}
                </div>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-10">
                {isDbLoading && PRODUCTS.length === 0 ? (
                    [1, 2, 3, 4, 5, 6].map(i => <ProductSkeleton key={i} isDark={isDark} />)
                ) : (
                    filteredProducts.map((product, index) => (
                        <div
                            key={product.id}
                            onClick={() => navigate(`/urunler/${product.slug || product.id}`)}
                            style={{ animationDelay: `${index * 100}ms` }}
                            className={`group relative rounded-[1.5rem] md:rounded-[2.5rem] p-4 md:p-8 transition-all duration-500 hover:scale-[1.02] border cursor-pointer animate-fadeIn opacity-0 [animation-fill-mode:forwards] flex flex-col ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-black/5 border-black/5 hover:bg-white hover:shadow-2xl'}`}
                        >
                            {/* Category Label */}
                            <div className="absolute top-4 left-4 md:top-8 md:left-8 flex flex-wrap gap-1 md:gap-2 z-10">
                                {product.categoryNames?.slice(0, 1).map((catName, idx) => (
                                    <span key={idx} className={`text-[8px] md:text-[10px] font-black uppercase tracking-[0.2em] px-2 py-0.5 md:px-3 md:py-1 rounded-full border bg-black/5 backdrop-blur-md ${isDark ? 'border-white/20 text-white/80' : 'border-black/10 text-slate-700'}`}>
                                        {catName}
                                    </span>
                                ))}
                                {product.categoryNames?.length > 2 && (
                                    <span className={`text-[10px] font-black uppercase tracking-[0.2em] px-2 py-1 rounded-full border ${isDark ? 'border-white/20 text-white/60' : 'border-black/10 text-slate-500'}`}>
                                        +{product.categoryNames.length - 2}
                                    </span>
                                )}
                            </div>

                            {/* Image Container */}
                            <div className="h-32 md:h-64 flex items-center justify-center my-4 md:my-8 transition-transform duration-700 group-hover:scale-110">
                                <img
                                    src={product.image}
                                    alt={product.name}
                                    className={`h-full w-auto object-contain scale-[0.85] ${isDark ? 'drop-shadow-[0_20px_40px_rgba(0,0,0,0.5)]' : 'drop-shadow-[0_20px_40px_rgba(0,0,0,0.15)]'}`}
                                    onError={(e) => { e.target.src = '/img/plastik-mat2.webp'; }}
                                />
                            </div>

                            {/* Content */}
                            <div className="space-y-2 md:space-y-4 flex-1 flex flex-col">
                                <h3 className="text-sm md:text-xl font-black uppercase tracking-tight leading-tight min-h-[2.5rem] md:min-h-[3rem]">
                                    {product.name?.toUpperCase()}
                                </h3>
                                <p className="text-[10px] md:text-sm opacity-60 line-clamp-2 md:line-clamp-2 font-medium">
                                    {product.desc}
                                </p>

                                <div className="flex items-center justify-end md:justify-between pt-4 md:pt-6 border-t border-white/10 mt-auto">
                                    <div
                                        className="flex items-center gap-1 md:gap-2 font-bold text-[10px] md:text-sm tracking-widest uppercase hover:gap-2 md:hover:gap-4 transition-all"
                                        style={{ color: activeBrand.primaryColor }}
                                    >
                                        <span className="hidden md:inline">İncele</span> <Search size={14} className="md:w-4 md:h-4" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Bottom Mascot Interaction */}
            <div className={`mt-24 p-12 rounded-[3.5rem] relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-10 ${isDark ? 'bg-gradient-to-br from-white/5 to-transparent border border-white/10' : 'bg-gradient-to-br from-black/5 to-transparent border border-black/5'}`}>
                <div className="relative z-10 max-w-xl text-center md:text-left">
                    <h2 className="text-3xl md:text-5xl font-black uppercase mb-4 leading-tight">İhtiyacınız olan <br /> çözümü bulamıyor musunuz?</h2>
                    <p className="opacity-70 text-lg mb-8">{activeBrand.name} profesyonel destek ekibi ile her zaman yanınızda.</p>
                    <button
                        className="px-10 py-5 text-white rounded-2xl font-black uppercase tracking-widest transition-all hover:scale-105 shadow-xl"
                        style={{ backgroundColor: activeBrand.primaryColor, boxShadow: `0 20px 25px -5px ${activeBrand.primaryColor}30` }}
                    >
                        Mağazalara Gözat
                    </button>
                </div>

                <div className="relative w-64 h-64 md:w-80 md:h-80">
                    <img
                        src={mascotImages[activeBrand.id] || mascotImages['dufa']}
                        alt={activeBrand.name}
                        className="w-full h-full object-contain animate-bounce-subtle drop-shadow-2xl"
                        onError={(e) => { e.target.src = mascotImages['dufa']; }}
                    />
                </div>
            </div>
        </div>
    );
};

export default Urunler;
